import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Toolbar from "@mui/material/Toolbar";
import {Close} from "@mui/icons-material";
import {IconButton, useMediaQuery, useTheme} from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import LoadingButton from "@mui/lab/LoadingButton";
import ErrorsCatcher from "@/components/ErrorsCatcher/Tsx/ErrorsCatcher";
import CreateOrEditFileMeaningForm
    from "@/features/InControlPanel/ReferenceBooks/LaboratoryClarifications/FileMeanings/CreateOrEditFileMeaningForm";
import {FormikProvider, useFormik} from "formik";
import {
    validationSchema
} from "@/features/InControlPanel/ReferenceBooks/LaboratoryClarifications/FileMeanings/CreateOrEditFileMeaningForm/validationSchema";
import {
    useCreateFileMeaningMutation
} from "@/api/ReferenceBooks/Lists/LaboratoryClarifications/FileMeanings/Create/CreateFileMeaningsApiSlice";
import {showPopup} from "@/features/PopupMessages/PopupMessages";
import {useDispatch, useSelector} from "react-redux";
import {
    FileMeaningsSelectors,
    reset,
    updateFields
} from "@/features/InControlPanel/ReferenceBooks/LaboratoryClarifications/FileMeanings/Slice/FileMeaningsSlice";
import {
    useUpdateFileMeaningMutation
} from "@/api/ReferenceBooks/Lists/LaboratoryClarifications/FileMeanings/Update/UpdateFileMeaningsApiSlice";
import {WithPermission} from "@/components/Permissions/WithPermission";

export interface CreateFileMeaningFormValues {
    title: string;
    alias: string;
    active: boolean;
}

function CreateOrEditFileMeaningDialog() {
    const {t} = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();

    const formVariant = useSelector(FileMeaningsSelectors.formVariant) || "new"
    const formVisible = useSelector(FileMeaningsSelectors.formVisible)
    const editId = useSelector(FileMeaningsSelectors.editId)
    const editTitle = useSelector(FileMeaningsSelectors.editTitle)
    const editAlias = useSelector(FileMeaningsSelectors.editAlias)
    const editActive = useSelector(FileMeaningsSelectors.editActive)

    const form = useFormik<CreateFileMeaningFormValues>({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        validationSchema,
        onSubmit,
    })

    const [createFileMeaning, {isLoading: createLoading}] = useCreateFileMeaningMutation();
    const [updateFileMeaning, {isLoading: updateLoading}] = useUpdateFileMeaningMutation();

    return (
      <>
          <WithPermission permission="file-meanings.add">
              <Button
                variant="contained"
                onClick={handleOpen}
              >
                  {t("Create new file meaning")}
              </Button>
          </WithPermission>

          <Dialog
            open={formVisible}
            onClose={handleClose}
            fullScreen={fullScreen}
          >
              <Toolbar sx={{display: "flex", justifyContent: "space-between"}}>
                  <DialogTitle sx={{flex: 1, textAlign: "center"}}>
                      {formVariant === "new" && (t("Creating new file meaning"))}
                      {formVariant === "edit" && (t("Editing file meaning"))}
                  </DialogTitle>
                  <IconButton onClick={handleClose}>
                      <Close/>
                  </IconButton>
              </Toolbar>

              <form style={{display: "contents"}} onSubmit={form.handleSubmit}>
                  <DialogContent>
                      <ErrorsCatcher errorId="fd3218d0-e134-4178-9200-bb5135aa5eae">
                          <FormikProvider value={form}>
                              <CreateOrEditFileMeaningForm/>
                          </FormikProvider>
                      </ErrorsCatcher>
                  </DialogContent>

                  <DialogActions>
                      <Button
                        onClick={handleClose}
                        variant="outlined"
                      >
                          {t("Cancel")}
                      </Button>

                      <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={form.isSubmitting || createLoading || updateLoading}
                      >
                          {formVariant === "new" && (t("Create"))}
                          {formVariant === "edit" && (t("Save changes"))}
                      </LoadingButton>
                  </DialogActions>
              </form>
          </Dialog>
      </>
    );

    function handleOpen() {
        dispatch(reset())
        dispatch(updateFields({
            formVisible: true
        }))
    }

    function handleClose() {
        dispatch(reset())
    }

    async function onSubmit(values: CreateFileMeaningFormValues) {
        try {
            if (formVariant === "new") {
                await createFileMeaning(values).unwrap()
            } else if (formVariant === "edit") {
                const payload = {id: editId, ...values}
                await updateFileMeaning(payload).unwrap()
            }

            showPopup({
                message: t("The request performed successfully."),
                type: "success",
                autoClose: 5000,
            });

            form.resetForm();
            dispatch(reset());
            handleClose();

        } catch (e) {
            const error = e as {
                data?: { message?: string; errors?: string[] }
            };

            const msg = error.data?.message;
            const errors = error.data?.errors ?? [];

            if (errors[0] === "Дублирование уникального значения") {
                form.setFieldError("alias", errors[0]);
            }

            if (msg) {
                showPopup({
                    message: `${t("Error")}.\n${msg}.\n${errors.join(", ")}`,
                    type: "error",
                    autoClose: 15000,
                });
                return;
            }

            showPopup({
                message: t("An error occured during the request."),
                type: "error",
                autoClose: 15000,
            });
        }
    }

    function getInitialValues() {
        if (formVariant === "edit") {
            return {
                title: editTitle,
                alias: editAlias,
                active: editActive,
            }
        }

        return {
            title: "",
            alias: "",
            active: true,
        }
    }
}

export default CreateOrEditFileMeaningDialog;
